import React from 'react';
import { Button } from '../ui/button';
import { Play, Pause, Square, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Wand2, Volume2, VolumeX } from 'lucide-react';
import { Slider } from '../ui/slider';
import { LS } from '../../utils/lang';

/**
 * Verse text panel + progress bar + play/pause/stop transport + active
 * ambient layer badges + master volume slider.
 */
const TransportControls = ({
  activeVerse,
  scope,
  hasChapter,
  chapterIdx,
  chapterVersesCount,
  progress,
  isPlaying,
  isLoadingTts,
  onPlayPause,
  onStop,
  activeEffects,
  volume,
  onVolumeChange,
  es = false,
}) => (
  <>
    {/* Verse Display */}
    <div className="bg-gradient-to-br from-primary/5 to-muted/40 p-4 rounded-lg border border-primary/10">
      <div className="text-xs font-semibold text-primary mb-2 tracking-wide uppercase flex items-center justify-between">
        <span data-testid="audio-current-reference">{activeVerse.reference}</span>
        {scope === 'chapter' && hasChapter && (
          <span className="text-muted-foreground tracking-normal normal-case">
            {es ? 'Versículo' : 'Verse'} {chapterIdx + 1} / {chapterVersesCount}
          </span>
        )}
      </div>
      <p className="text-sm text-foreground leading-relaxed">{activeVerse.text}</p>
    </div>

    {/* Progress */}
    <div className="space-y-2">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{es ? 'Progreso de escucha' : 'Listening progress'}</span>
        <span data-testid="audio-progress-value">{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
        <div
          className="bg-gradient-to-r from-primary to-primary/60 h-2 transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>

    {/* Transport */}
    <div className="flex items-center justify-center gap-4">
      <Button
        onClick={onStop}
        disabled={!isPlaying && progress === 0}
        variant="outline"
        size="icon"
        data-testid="audio-stop-btn"
        aria-label={LS('Stop', 'Detener')}
      >
        <Square className="w-4 h-4" />
      </Button>

      <Button
        onClick={onPlayPause}
        size="lg"
        className="w-16 h-16 rounded-full shadow-lg"
        disabled={isLoadingTts}
        data-testid="audio-play-pause-btn"
        aria-label={isPlaying ? LS('Pause', 'Pausar') : LS('Play', 'Reproducir')}
      >
        {isLoadingTts ? (
          <Loader2 className="w-7 h-7 animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-8 h-8" />
        ) : (
          <Play className="w-8 h-8 ml-1" />
        )}
      </Button>

      <div className="w-10" />
    </div>

    {/* Active ambient layers */}
    {activeEffects.length > 0 && (
      <div className="flex flex-wrap gap-2 justify-center" data-testid="active-effects">
        {activeEffects.map((e) => (
          <Badge key={e} variant="secondary" className="text-xs">
            <Wand2 className="w-3 h-3 mr-1" />
            {e}
          </Badge>
        ))}
      </div>
    )}

    {/* Volume */}
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium flex items-center gap-2">
          {volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          {es ? 'Volumen' : 'Volume'}
        </span>
        <span className="text-xs text-muted-foreground">{Math.round(volume * 100)}%</span>
      </div>
      <Slider
        value={[volume]}
        onValueChange={(v) => onVolumeChange(v[0])}
        max={1}
        step={0.05}
        className="w-full"
        data-testid="audio-volume-slider"
      />
    </div>
  </>
);

export default TransportControls;
